import * as React from 'react'
import { styled } from 'next-yak'

import * as behaviours from '../../../behaviours'
import * as S from '../../../styles'

import { Field } from './Field'
import { FieldStatus } from './FieldStatus'
import { type SelectProps, Select } from '../select/Select'

export type SelectFieldProps = {
  className?: string
  /** set if you need a specific value for the id (used internally by `<label for={id} />`)
   * @default String(Math.random()) */
  id?: string
  /** short label of the field */
  label?: React.ReactNode
  /** short description of the field */
  description?: React.ReactNode
  /** an error message (toggle the visual status of the field) */
  error?: React.ReactNode
  /** short context of the field (meta info, like optional) */
  context?: string
  children: React.ReactNode
  format?: 'wide'
  /** selectLabel for the `<SelectButton>` element */
  selectLabel?: (selectedOptions: { label: string }[], placeholder: React.ReactNode) => React.ReactNode
} & Omit<SelectProps, 'status' | 'label'>

function SelectFieldComponent({
  className,
  style,
  label,
  description,
  error,
  id,
  children,
  context,
  format,
  selectLabel,
  ...selectProps
}: SelectFieldProps): React.ReactElement<SelectFieldProps> {
  const { classList, status } = behaviours.getStatusClassName(className, error)

  const stableId = React.useId()

  return (
    <SelectFieldWrapper
      className={classList.filter(Boolean).join(' ')}
      style={style}
      context={context}
      description={description}
      disabled={selectProps.disabled}
      htmlFor={id ?? stableId}
      label={label}
      format={format}
    >
      <Select {...selectProps} label={selectLabel} status={status === 'success' ? undefined : status} id={id}>
        {children}
      </Select>
      {error && <FieldStatus error={error} />}
    </SelectFieldWrapper>
  )
}

/** extends `<Select>`, see there for additional props */
export const SelectField = styled(SelectFieldComponent)<SelectFieldProps>``
SelectField.displayName = 'SelectField'

export const SelectFieldWrapper = styled(Field)`
  ${S.field.Status} {
    margin-top: 10px;
  }

  ${S.field.Title}, ${S.field.Description} {
    margin-bottom: 7px;
  }
`
